import { useSelector } from 'react-redux';
import {
    ConfigurationCategoriesData,
    ConfigurationCategoriesDataGroupCategory,
} from '@placer-ui/types';

import {
    selectConfigurationCategories,
    selectCategoriesData,
    selectCategoriesWithChains,
    selectPrimaryCategoriesData,
    selectAvailableCategoriesData,
    selectAvailableCategoriesWithChains,
    selectAvailableSubCategoryNames,
    selectAvailablePrimaryCategories,
    selectIsCategoriesInitialized,
    removeInvalidCategoriesRecursive,
    getSubCategoriesNamesRecursive,
} from './configuration-categories-selectors';
import { useMemo } from 'react';
import { PROPERTY_CATEGORY_SECTION } from 'store/configuration/categories/consts';

export const useSelectConfigurationCategories = () => useSelector(selectConfigurationCategories);
export const useSelectCategoriesData = () =>
    useSelector(selectCategoriesData) as ConfigurationCategoriesData[];
export const useSelectCategoriesWithChains = () =>
    useSelector(selectCategoriesWithChains) as ConfigurationCategoriesDataGroupCategory[];
export const useSelectPrimaryCategoriesData = () =>
    useSelector(selectPrimaryCategoriesData) as ConfigurationCategoriesData[];

export const useSelectAvailableCategoriesData = () =>
    useSelector(selectAvailableCategoriesData) as ConfigurationCategoriesData[];
export const useSelectAvailableCategoriesWithChains = () =>
    useSelector(selectAvailableCategoriesWithChains) as ConfigurationCategoriesDataGroupCategory[];
export const useSelectAvailableSubCategoryNames = () =>
    useSelector(selectAvailableSubCategoryNames) as string[];
export const useSelectAvailablePrimaryCategories = () =>
    useSelector(selectAvailablePrimaryCategories);
export const useSelectIsCategoriesInitialized = () => useSelector(selectIsCategoriesInitialized);

export const useGetAvailableCategories = (sections: string[] = []) => {
    const categoriesData = useSelectCategoriesData();

    return useMemo(() => {
        return removeInvalidCategoriesRecursive(categoriesData, false, sections);
    }, [categoriesData, sections]);
};

export const useGetAvailablePrimaryCategories = (sections: string[]) => {
    const primaryCategories = useSelectPrimaryCategoriesData();

    return useMemo(() => {
        return removeInvalidCategoriesRecursive(primaryCategories, false, sections);
    }, [primaryCategories, sections]);
};

export const useGetAvailableSubCategories = (sections: string[]) => {
    const categoriesData = useSelectCategoriesData();

    return useMemo(() => {
        return getSubCategoriesNamesRecursive(
            removeInvalidCategoriesRecursive(categoriesData, false, sections),
        );
    }, [categoriesData, sections]);
};

export const useGetAvailableCategoriesWithChains = (sections: string[]) => {
    const categoriesWithChain = useSelectCategoriesWithChains();

    return useMemo(() => {
        return removeInvalidCategoriesRecursive(categoriesWithChain, true, sections);
    }, [categoriesWithChain, sections]);
};

export const useGetAvailableRegionSubCategories = () => {
    const categoriesData = useSelectCategoriesData();

    return useMemo(() => {
        const regionCategories = categoriesData.filter((group) => group.label === 'Region');

        return getSubCategoriesNamesRecursive(
            removeInvalidCategoriesRecursive(regionCategories, false, PROPERTY_CATEGORY_SECTION),
        );
    }, [categoriesData]);
};
